import { useParams, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';

// 🚀 LAZY LOAD ALL HEAVY COMPONENTS
const FoundationRouter = lazy(() => import('./FoundationRouter'));
const ExternalAssessmentPage = lazy(() => import('./ExternalAssessmentPage'));
const InternalAssessmentPage = lazy(() => import('./InternalAssessmentPage'));
const FormulationRouter = lazy(() => import('./formulation/FormulationRouter'));

// Loading component with skeleton UI
const LoadingFallback = ({ stepName }: { stepName: string }) => (
  <div className="min-h-[50vh] bg-gray-50">
    <div className="bg-white border-b border-gray-200 animate-pulse">
      <div className="px-8 py-6">
        <div className="flex items-center space-x-4">
          <div className="h-16 w-16 bg-gray-200 rounded-xl"></div>
          <div>
            <div className="h-8 w-64 max-w-full bg-gray-200 rounded mb-2"></div>
            <div className="h-4 w-48 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    </div>

    <div className="px-8 py-8">
      <div className="space-y-6">
        <div className="bg-white rounded-lg p-6 animate-pulse">
          <div className="h-6 w-48 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 w-full bg-gray-100 rounded"></div>
            <div className="h-4 w-3/4 bg-gray-100 rounded"></div>
            <div className="h-4 w-1/2 bg-gray-100 rounded"></div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 animate-pulse">
          <div className="h-6 w-56 max-w-full bg-gray-200 rounded mb-4"></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="h-32 bg-gray-100 rounded"></div>
            <div className="h-32 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>

      <div className="mt-6 text-center">
        <div className="inline-flex items-center space-x-2 text-blue-600">
          <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
          <span className="text-sm font-medium">Loading {stepName}...</span>
        </div>
      </div>
    </div>
  </div>
);

function FoundationStep() {
  return (
    <Suspense fallback={<LoadingFallback stepName="Foundation" />}>
      <FoundationRouter />
    </Suspense>
  );
}

function ExternalAssessmentStep() {
  return (
    <Suspense fallback={<LoadingFallback stepName="External Assessment" />}>
      <ExternalAssessmentPage />
    </Suspense>
  );
}

function InternalAssessmentStep() {
  return (
    <Suspense fallback={<LoadingFallback stepName="Internal Assessment" />}>
      <InternalAssessmentPage />
    </Suspense>
  );
}

function FormulationStep() {
  return (
    <Suspense fallback={<LoadingFallback stepName="Strategy Formulation" />}>
      <FormulationRouter />
    </Suspense>
  );
}

function ExecutionStep() {
  const { companyId } = useParams<{ companyId: string }>();
  return <Navigate to={`/company/${companyId}/execution`} replace />;
}

export default function StrategyStepRouter() {
  const { step } = useParams<{ step: string }>();

  if (!step) {
    return <Navigate to="." replace />;
  }

  switch (step) {
    case 'foundation':
      return <FoundationStep />;
    case 'external-assessment':
      return <ExternalAssessmentStep />;
    case 'internal-assessment':
      return <InternalAssessmentStep />;
    case 'formulation':
      return <FormulationStep />;
    case 'execution':
      return <ExecutionStep />;
    default:
      return <Navigate to="." replace />;
  }
}
